import React, { useEffect, useState } from 'react';
import { Paper, Popover, Stack, Typography } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { SnappingIcon } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { EntitySnapTargetType } from '../../../library/layout-snapping/EntitySnapTarget';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import { getHiddenEntitySnapTargetTypesForVisibleObjects } from '../../../store/selectors/lineSnappingSelectors';
import { getActiveSnappingContextOrNull } from '../../../store/selectors/snappingSettingsSelectors';
import { layoutSlice, SnappingSettingsContext } from '../../../store/slices/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { InteractiveEditSnappingSettingsControls } from '../../misc/floating-boxes/snapping-settings/controls/InteractiveEditSnappingSettingsControls';
import { PointSnappingSettingsControls } from '../../misc/floating-boxes/snapping-settings/controls/PointSnappingSettingsControls';
import { SnappingSettingsResetButton } from '../../misc/floating-boxes/snapping-settings/controls/SnappingSettingsResetButton';
import { SnappingTargetControls } from '../../misc/floating-boxes/snapping-settings/controls/SnappingTargetControls';
import { ToolbarIconButton } from '../top/ToolbarIconButton';

const SNAPPING_SETTINGS_CONTEXTS = [
  'interactiveEdit',
  'bearingEnvelope',
  'trussEnvelope',
  'general',
] as const satisfies readonly SnappingSettingsContext[];

function snappingSettingsContextToHumanReadableString(context: SnappingSettingsContext) {
  switch (context) {
    case 'bearingEnvelope':
      return 'beam or wall point selection';

    case 'general':
      return 'all other contexts';

    case 'trussEnvelope':
      return 'truss envelope point selection';

    case 'interactiveEdit':
      return 'interactive beam or wall dragging';

    default:
      assertExhaustiveSwitch(context);
  }
}

export function SnappingSettingsButton() {
  const dispatch = useAppDispatch();
  const inLayoutView = useAppSelector(isLayoutViewActive);
  const activeContext = useAppSelector(getActiveSnappingContextOrNull);
  const hiddenTypes = useAppSelector(getHiddenEntitySnapTargetTypesForVisibleObjects);

  const [selectedContext, setSelectedContext] = useState<SnappingSettingsContext>(activeContext ?? 'general');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (activeContext != null) setSelectedContext(activeContext);
  }, [activeContext]);

  const selectedPointContext = selectedContext === 'interactiveEdit' ? 'general' : selectedContext;
  const pointSettings = useAppSelector((state) => state.layout.pointSnappingSettings[selectedPointContext]);
  const interactiveEditSettings = useAppSelector((state) => state.layout.interactiveEditSnappingSettings);

  if (!inLayoutView) return null;

  const popoverOpen = anchorEl != null;

  function togglePopover(event: React.MouseEvent<HTMLElement>) {
    if (popoverOpen) {
      closePopover();
      return;
    }

    setAnchorEl(event.currentTarget);
  }

  function closePopover() {
    setAnchorEl(null);
  }

  function toggleTarget(target: EntitySnapTargetType) {
    if (selectedContext === 'interactiveEdit') {
      dispatch(layoutSlice.actions.interactiveEditSnappingTargetToggled(target));
    } else {
      dispatch(layoutSlice.actions.pointSnappingTargetToggled({ context: selectedContext, target }));
    }
  }

  return (
    <>
      {popoverOpen && (
        <Popover
          open
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Paper
            className="clickable-during-picking"
            elevation={1}
            sx={{ width: 300, maxHeight: 'calc(100vh - 96px)', overflowY: 'auto', padding: 2 }}
          >
            <Stack direction="column" spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Snapping Settings</Typography>

                {selectedContext === 'interactiveEdit' ? (
                  <SnappingSettingsResetButton context="interactiveEdit" settings={interactiveEditSettings} />
                ) : (
                  <SnappingSettingsResetButton context={selectedContext} settings={pointSettings} />
                )}
              </Stack>

              <PropertySelectField
                property="Context"
                value={selectedContext}
                options={SNAPPING_SETTINGS_CONTEXTS}
                getName={snappingSettingsContextToHumanReadableString}
                submit={setSelectedContext}
                margin="none"
              />

              {selectedContext === 'interactiveEdit' ? (
                <InteractiveEditSnappingSettingsControls settings={interactiveEditSettings} />
              ) : (
                <PointSnappingSettingsControls context={selectedContext} settings={pointSettings} />
              )}

              <SnappingTargetControls
                settings={selectedContext === 'interactiveEdit' ? interactiveEditSettings : pointSettings}
                onTargetToggled={toggleTarget}
                disabledTypes={hiddenTypes}
              />
            </Stack>
          </Paper>
        </Popover>
      )}

      <ToolbarIconButton
        title="Snapping Settings"
        icon={<SnappingIcon />}
        clickableDuringPicking
        toggleValue={popoverOpen}
        onClick={togglePopover}
      />
    </>
  );
}
